import React, { memo, useState, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button, FormControl, Grid, LinearProgress, withTheme } from '@material-ui/core';
import { CardElement } from '@basis-theory/react-elements';
import flowRight from 'lodash-es/flowRight';
import { paymentMethod as PaymentMethod } from '@ourbranch/lookups';
import { observer } from 'mobx-react';

import { Label } from 'core';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { useBasisTheoryElementRefs } from 'common/hooks/basis-theory-elements.context';
import { useCallRecordingPause } from 'common/hooks/use-call-recording-pause';
import { ActionButton } from 'core/components/action-button';
import useStyles, { styles } from './credit-card-form.styles';

const codeToPaymentMethodMap = {
  [PaymentMethod.ACH]: 'Bank account',
  [PaymentMethod.CreditCard]: 'Credit card',
  [PaymentMethod.Escrow]: 'Escrow'
};

function CreditCardForm({
  theme,
  close,
  buttonText,
  onSubmit,
  showCloseButton,
  label,
  fromModal,
  closeModal,
  children
}) {
  const [errors, setErrors] = useState(undefined);
  const [completed, setCompleted] = useState(undefined);
  const [cardData, setCardData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const handleCardFocus = useCallback(() => setCardFocused(true), []);
  const handleCardBlur = useCallback(() => setCardFocused(false), []);
  const classes = useStyles({ fromModal });
  const inlineStyles = styles(theme);
  const { cardElementId } = useBasisTheoryToken();
  const { cardRef } = useBasisTheoryElementRefs();
  const { account: accountStore } = useStore();
  const session = useSession();

  // Pause Dialpad recording while the full card number is on screen (internal agents).
  useCallRecordingPause();

  const handleChange = (changeEvent) => {
    const { complete, errors, cardBrand } = changeEvent;
    setCompleted(Boolean(complete));
    setCardData({ brand: cardBrand });
    if (errors.length) {
      const errorMessages = errors.map((err) => `${err.type} ${err.targetId}`).join('. ');
      setErrors(errorMessages);
    } else {
      setErrors(undefined);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit(cardData);
    if (fromModal) {
      const now = new Date();
      await accountStore.sendEventAsMember({
        eventName: 'Credit card added',
        properties: JSON.stringify({
          policyId: accountStore.policies.policy.policy.id,
          staffUser: session.user.username,
          timeStamp: now.toISOString(),
          paymentMethod: codeToPaymentMethodMap[accountStore.policies.policy.policy.paymentMethod]
        }),
        userId: accountStore.id
      });
      closeModal();
    }
    setLoading(false);
  };

  if (fromModal) {
    return (
      <>
        <Label type="coverageLabelDark" className={classes.label}>
          {label}
        </Label>
        <Grid container alignItems="center" spacing={2} justifyContent="flex-end">
          <div className="HIDDEN_FROM_SCREEN_SHARING" style={{ width: '100%' }}>
            <div className={classNames(classes.cardElementWrapper, { [classes.cardElementFocused]: cardFocused })}>
              <CardElement
                id={cardElementId}
                ref={cardRef}
                onChange={handleChange}
                onFocus={handleCardFocus}
                onBlur={handleCardBlur}
                style={inlineStyles.cardElementStyle}
              />
            </div>
          </div>
          {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
          {children}
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            className={classes.submit}
            disabled={loading || errors || !completed}
          >
            {buttonText}
          </Button>
        </Grid>
      </>
    );
  }

  return (
    <Grid direction="column" container>
      <Grid container item alignItems="center" className={classes.container}>
        <FormControl className={classes.cardContainer}>
          <Label type="coverageLabel" className={classes.label}>
            {label}
          </Label>
          <div className="HIDDEN_FROM_SCREEN_SHARING" style={{ width: '100%' }}>
            <div className={classNames(classes.cardElementWrapper, { [classes.cardElementFocused]: cardFocused })}>
              <CardElement
                id={cardElementId}
                ref={cardRef}
                onChange={handleChange}
                onFocus={handleCardFocus}
                onBlur={handleCardBlur}
                style={inlineStyles.cardElementStyle}
              />
            </div>
          </div>
        </FormControl>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          className={classes.submit}
          disabled={loading || errors || !completed}
        >
          {buttonText}
        </Button>
        {showCloseButton && <ActionButton type="close" alt="close form" onClick={close} disabled={loading} />}
      </Grid>
      {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
    </Grid>
  );
}

CreditCardForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  close: PropTypes.func,
  buttonText: PropTypes.string,
  showCloseButton: PropTypes.bool,
  label: PropTypes.string,
  fromModal: PropTypes.bool,
  closeModal: PropTypes.func,
  children: PropTypes.node
};

CreditCardForm.defaultProps = {
  buttonText: 'Confirm',
  showCloseButton: false,
  close: () => {},
  label: 'Credit Card',
  fromModal: false,
  closeModal: () => {}
};

export default flowRight(memo, withTheme, observer)(CreditCardForm);
